import React from 'react';
import scrollbarSize from 'dom-helpers/scrollbarSize';

import {
	AbstractCheckboxFieldCheckedState,
} from '~/components/patterns/forms/fields/AbstractCheckboxField';
import CheckboxGroupContainer from '~/components/logic/checkboxGroup/CheckboxGroupContainer';
import CheckboxInGroup from '~/components/logic/checkboxGroup/CheckboxInGroup';
import DatatableBodyCell, {
	DatatableBodyCellAlignment,
	DatatableBodyCellPadding,
	DatatableBodyCellSize,
} from '~/components/patterns/tables/datatables/cells/DatatableBodyCell';
import DatatableHeaderCell, {
	DatatableHeaderCellAlignment,
	DatatableHeaderCellSize,
} from '~/components/patterns/tables/datatables/cells/DatatableHeaderCell';
import DatatableLayout from '~/components/patterns/tables/datatables/DatatableLayout';
import Ellipsis from '~/components/patterns/values/Ellipsis';
import FixedHeaderGrid from '~/components/patterns/tables/datatables/FixedHeaderGrid';
import MasterCheckbox from '~/components/logic/checkboxGroup/MasterCheckbox';
import TableLabel from '~/components/patterns/tables/datatables/parts/TableLabel';

import useFormContext from '~/hooks/useFormContext';

import getArrayItemAtSafeIndex from '~/utilities/getArrayItemAtSafeIndex';



const HEADER_HEIGHT = 66;
const ROW_HEIGHT = 40;
const TABLE_HEIGHT = 330;
const CHECKBOX_CELL_WIDTH = 100;
const LABEL_CELL_WIDTH = 150;



type Props = {
	columns: ReadonlyArray<{
		label: React.ReactNode,
		name: string,
	}>,
	label: React.ReactNode,
	name: string,
	rows: ReadonlyArray<{
		disabled?: boolean,
		label: React.ReactNode,
		name: string,
	}>,
	/** Possibility to change default table height */
	tableHeight?: number,
	tableWidth: number,
};

const MultiselectListTable: React.FC<Props> = (props) => {
	const {
		columns,
		label,
		name,
		rows,
		tableHeight = TABLE_HEIGHT,
		tableWidth,
	} = props;

	const formContext = useFormContext();

	const formContextOnMountHandler = formContext.onMountHandler;
	const formContextOnUnmountHandler = formContext.onUnmountHandler;

	const [highlightedRow, setHighlightedRow] = React.useState<number | null>(null);

	const gridRef = React.useRef<any>(null);

	// Stabilize scrollbar decision to prevent feedback loop
	// Track previous content dimensions and table width to only recalculate when content actually changes
	const previousContentRef = React.useRef<{
		calculatedHeight: number,
		tableHeight: number,
		tableWidth: number,
		needsScrollbar: boolean | null,
	} | null>(null);

	// Stabilize tableWidth to prevent rapid changes from causing Grid errors
	// Use percentage-based threshold (1% of width) to handle extreme zoom levels elegantly
	const stableTableWidthRef = React.useRef<number | null>(null);
	const stableTableWidth = React.useMemo(() => {
		const isValid = tableWidth && tableWidth > 0 && Number.isFinite(tableWidth);
		if (!isValid) {
			return stableTableWidthRef.current ?? 0;
		}

		const previous = stableTableWidthRef.current;
		if (previous === null) {
			stableTableWidthRef.current = tableWidth;
			return tableWidth;
		}

		// Calculate percentage change - use 1% threshold for stability
		// This works better at extreme zoom levels than fixed pixel thresholds
		const changePercent = Math.abs((previous - tableWidth) / previous) * 100;
		const thresholdPercent = 1; // 1% change threshold

		// Also check absolute change as fallback for very small widths
		const absoluteChange = Math.abs(previous - tableWidth);
		const absoluteThreshold = Math.max(5, previous * 0.01); // At least 5px or 1% of width

		if (changePercent > thresholdPercent || absoluteChange > absoluteThreshold) {
			stableTableWidthRef.current = tableWidth;
			return tableWidth;
		}

		return previous;
	}, [tableWidth]);

	React.useEffect(
		() => {
			formContextOnMountHandler(name, {
				setValues: true,
			});

			return () => {
				formContextOnUnmountHandler(name);
			};
		},
		[
			formContextOnMountHandler,
			formContextOnUnmountHandler,
			name,
		],
	);

	const handleTableMouseEnter = React.useCallback(
		(rowIndex) => {
			setHighlightedRow(rowIndex);

			gridRef.current?.refs.BodyGrid?.forceUpdate();
		},
		[],
	);

	const handleTableMouseLeave = React.useCallback(
		(rowIndex) => {
			if (highlightedRow === rowIndex) {
				setHighlightedRow(null);

				gridRef.current?.refs.BodyGrid?.forceUpdate();
			}
		},
		[
			highlightedRow,
		],
	);

	const getColumnWidth = React.useCallback(
		({ index }) => {
			if (index > 0) {
				return CHECKBOX_CELL_WIDTH;
			}

			// Use stabilized tableWidth to prevent rapid recalculations
			const widthToUse = stableTableWidth;

			// Safety check: ensure tableWidth is a valid number
			if (!widthToUse || widthToUse <= 0 || !Number.isFinite(widthToUse)) {
				return LABEL_CELL_WIDTH;
			}

			const calculatedMinWidth = columns.length * CHECKBOX_CELL_WIDTH + LABEL_CELL_WIDTH;

			if (calculatedMinWidth > widthToUse) {
				return LABEL_CELL_WIDTH;
			}

			const calculatedHeight = ROW_HEIGHT * rows.length;
			let widthOffset = -2; // -2 is because of table border around this component

			// Stabilize scrollbar decision to prevent feedback loop
			// Only recalculate when content dimensions actually change, not on every width measurement
			const needsScrollbar = tableHeight < calculatedHeight;
			const previousContent = previousContentRef.current;
			const scrollbarSizeValue = scrollbarSize();

			// Check if content dimensions have actually changed
			const contentDimensionsChanged = (
				previousContent === null
				|| previousContent.calculatedHeight !== calculatedHeight
				|| previousContent.tableHeight !== tableHeight
			);

			// Detect potential feedback loop: if width changed by exactly scrollbarSize and we're toggling scrollbar
			// This indicates the width change was caused by scrollbar appearance/disappearance, not real layout change
			const widthChangedByScrollbarSize = previousContent !== null
				&& Number.isFinite(widthToUse)
				&& Math.abs(previousContent.tableWidth - widthToUse) === scrollbarSizeValue;

			const isLikelyFeedbackLoop = widthChangedByScrollbarSize
				&& previousContent.needsScrollbar !== null
				&& previousContent.needsScrollbar !== needsScrollbar;

			// Only recalculate if content actually changed AND it's not a feedback loop
			const shouldRecalculate = contentDimensionsChanged && !isLikelyFeedbackLoop;

			// Use previous scrollbar state if we shouldn't recalculate, otherwise use current calculation
			const shouldUseScrollbar = shouldRecalculate
				? needsScrollbar
				: (previousContent.needsScrollbar ?? needsScrollbar);

			// Only update ref when content changes or when we recalculate
			// This prevents ref from being updated on every tableWidth change from Measurer
			if (shouldRecalculate) {
				previousContentRef.current = {
					calculatedHeight,
					tableHeight,
					tableWidth: widthToUse,
					needsScrollbar: shouldUseScrollbar,
				};
			}

			if (shouldUseScrollbar) {
				widthOffset -= scrollbarSize();
			}

			const calculatedWidth = widthToUse - (columns.length * CHECKBOX_CELL_WIDTH) + widthOffset;

			// Safety check: ensure we return a valid positive number
			// Use previous width if current calculation is invalid to prevent Grid errors
			if (!Number.isFinite(calculatedWidth) || calculatedWidth <= 0) {
				return previousContent !== null && Number.isFinite(previousContent.tableWidth)
					? Math.max(LABEL_CELL_WIDTH, previousContent.tableWidth - (columns.length * CHECKBOX_CELL_WIDTH) + widthOffset)
					: LABEL_CELL_WIDTH;
			}

			return Math.max(LABEL_CELL_WIDTH, calculatedWidth);
		},
		[
			columns,
			rows,
			tableHeight,
			stableTableWidth,
		],
	);

	const calculateTableHeight = React.useCallback(
		() => {
			const calculated = ROW_HEIGHT * rows.length;

			if (tableHeight > calculated) {
				return calculated + HEADER_HEIGHT;
			}

			return tableHeight + HEADER_HEIGHT;
		},
		[
			rows,
			tableHeight,
		],
	);

	const renderCell = React.useCallback(
		({ columnIndex, key, rowIndex, style }) => {
			const row = getArrayItemAtSafeIndex(rows, rowIndex);

			let cellContent: React.ReactNode;

			if (columnIndex === 0) {
				cellContent = (
					<Ellipsis>
						{row.label}
					</Ellipsis>
				);
			} else if (columnIndex <= columns.length) {
				const column = getArrayItemAtSafeIndex(columns, columnIndex - 1);

				cellContent = (
					<CheckboxInGroup
						centerAligned={true}
						name={column.name + '__' + row.name}
					/>
				);
			}

			return (
				<DatatableBodyCell
					alignment={columnIndex !== 0 ? DatatableBodyCellAlignment.Center : DatatableBodyCellAlignment.Left}
					cssStyle={style}
					isInHighlightedRow={highlightedRow === rowIndex}
					key={key}
					onMouseEnterCallback={handleTableMouseEnter.bind(null, rowIndex)}
					onMouseLeaveCallback={handleTableMouseLeave.bind(null, rowIndex)}
					padding={columnIndex !== 0 ? DatatableBodyCellPadding.CheckboxPadding : DatatableBodyCellPadding.Default}
					rowIndex={rowIndex}
					separator={columnIndex !== 0 && columnIndex <= columns.length}
					size={DatatableBodyCellSize.Small}
				>
					{cellContent}
				</DatatableBodyCell>
			);
		},
		[
			columns,
			handleTableMouseEnter,
			handleTableMouseLeave,
			rows,
			highlightedRow,
		],
	);

	const renderHeader = React.useCallback(
		({ columnIndex }) => {
			let cellContent;
			let checkbox;

			if (columnIndex === 0) {
				cellContent = label;
			} else {
				const column = getArrayItemAtSafeIndex(columns, columnIndex - 1);

				cellContent = column.label;
				checkbox = (
					<MasterCheckbox
						items={rows.map((row) => column.name + '__' + row.name)}
						name={column.name}
					/>
				);
			}

			const isCenterAligned = columnIndex !== 0 && columnIndex <= columns.length;

			return (
				<DatatableHeaderCell
					alignment={isCenterAligned ? DatatableHeaderCellAlignment.Center : DatatableHeaderCellAlignment.Left}
					key={'header_' + columnIndex}
					separator={isCenterAligned}
					size={DatatableHeaderCellSize.Small}
					width={getColumnWidth({ index: columnIndex })}
				>
					<TableLabel
						centerAligned={isCenterAligned}
						footer={checkbox}
						label={cellContent}
					/>
				</DatatableHeaderCell>
			);
		},
		[
			columns,
			getColumnWidth,
			label,
			rows,
		],
	);

	const calculatedTableHeight = calculateTableHeight();
	const columnCount = 1 + columns.length;

	// -2 is because of table border around this component
	const tableWidthOffset = -2;

	const allValue: Array<string> = [];
	const indeterminate: Array<string> = [];

	for (const column in formContext.values[name]) {
		if (formContext.values[name].hasOwnProperty(column)) {
			for (const row in formContext.values[name][column]) {
				if (formContext.values[name][column].hasOwnProperty(row)) {
					if (formContext.values[name][column][row] === AbstractCheckboxFieldCheckedState.Indeterminate) {
						indeterminate.push(column + '__' + row);
					} else if (formContext.values[name][column][row]) {
						allValue.push(column + '__' + row);
					}
				}
			}
		}
	}

	const allOptions: Array<Array<string>> = [];
	const disabledOptions: Array<string> = [];

	rows.forEach((row, rowIndex) => {
		const rowOptions: Array<string> = [];

		columns.forEach((column, columnIndex) => {
			rowOptions[columnIndex] = column.name + '__' + row.name;

			if (row.disabled) {
				disabledOptions.push(column.name + '__' + row.name);
			}
		});

		allOptions[rowIndex] = rowOptions;
	});

	// Ensure tableWidth is valid before rendering Grid to prevent react-virtualized errors
	const isValidTableWidth = stableTableWidth && stableTableWidth > 0 && Number.isFinite(stableTableWidth);

	// Use a stable key based on content, not tableWidth, to prevent unnecessary remounts
	// Only change key when content actually changes
	const gridKey = React.useMemo(
		() => `${rows.length}-${columns.length}-${tableHeight}`,
		[rows.length, columns.length, tableHeight],
	);

	return (
		<DatatableLayout maxWidth={isValidTableWidth ? stableTableWidth : undefined}>
			<CheckboxGroupContainer
				disabled={disabledOptions}
				indeterminate={indeterminate}
				name={name}
				onChangeCallback={({ checked, unchecked }) => {
					const result = {
						disabled: {},
					};

					columns.forEach((column) => {
						result[column.name] = {};

						rows.forEach((row) => {
							const fieldName = column.name + '__' + row.name;

							if (checked.includes(fieldName)) {
								result[column.name][row.name] = true;
							} else if (unchecked.includes(fieldName)) {
								result[column.name][row.name] = false;
							} else {
								result[column.name][row.name] = AbstractCheckboxFieldCheckedState.Indeterminate;
							}

							result.disabled[row.name] = row.disabled;
						});
					});

					formContext.onChangeHandler(name, result);
				}}
				optionGroups={allOptions}
				value={allValue}
			>
				{isValidTableWidth && (
					<FixedHeaderGrid
						bodyCellRenderer={renderCell}
						columnCount={columnCount}
						columnWidth={getColumnWidth}
						data={{
							data: formContext.values[name],
						}}
						headerCellRenderer={renderHeader}
						headerHeight={HEADER_HEIGHT}
						height={calculatedTableHeight}
						key={gridKey}
						overscanColumnCount={columnCount}
						ref={gridRef}
						rowCount={rows.length}
						rowHeight={ROW_HEIGHT}
						width={stableTableWidth + tableWidthOffset}
					/>
				)}
			</CheckboxGroupContainer>
		</DatatableLayout>
	);
};



export default MultiselectListTable;

export {
	AbstractCheckboxFieldCheckedState as MultiselectFieldTableCheckedState,
};
